import {DateTime, ToISOTimeOptions} from 'luxon';
import {createParser} from 'nuqs';

// Format constants
// ---------------------------------------------------------------

// https://github.com/moment/luxon/blob/master/docs/formatting.md#table-of-tokens

export const DEFAULT_TIME_ZONE: string = 'UTC';

// October 7, 2026
export const DATE_LONG_FORMAT: string = 'MMMM d, yyyy';

// Oct 7, 2026
export const DATE_MED_FORMAT: string = 'MMM d, yyyy';

// 2026-10-07
export const DATE_ISO_FORMAT: string = 'yyyy-MM-dd';

// 13:35
export const TIME_FORMAT: string = 'HH:mm';

// 13:35:07
export const TIME_FORMAT_WITH_SECONDS: string = 'HH:mm:ss';

// Utility functions
// ---------------------------------------------------------------

type FormatDateOptions = {
  withTime?: boolean;
  withZone?: boolean;
  withSeconds?: boolean;
};

const formatDate = (
  date?: string | null,
  baseFormat?: string,
  {
    withTime = false,
    withZone = false,
    withSeconds = false,
  }: FormatDateOptions = {},
): string => {
  const parts: string[] = baseFormat ? [baseFormat] : [];

  if (withTime) {
    if (baseFormat) {
      parts.push("'at'"); // Separator when there's date + time together
    }

    parts.push(withSeconds ? TIME_FORMAT_WITH_SECONDS : TIME_FORMAT);

    if (withZone) {
      parts.push('ZZZZ');
    }
  }

  return date
    ? DateTime.fromISO(date, {zone: DEFAULT_TIME_ZONE}).toFormat(
        parts.join(' '),
      )
    : '';
};

export const formatDateLong = (
  date?: string | null,
  options: FormatDateOptions = {},
): string => formatDate(date, DATE_LONG_FORMAT, options);

export const formatDateMed = (
  date?: string | null,
  options: FormatDateOptions = {},
): string => formatDate(date, DATE_MED_FORMAT, options);

export const formatDateISO = (date?: string | null): string =>
  formatDate(date, DATE_ISO_FORMAT);

export const formatTime = (
  date?: string | null,
  options: Omit<FormatDateOptions, 'withTime'> = {},
): string => formatDate(date, undefined, {withTime: true, ...options});

export const formatDateRelative = (date?: string | null): string | null =>
  date ? DateTime.fromISO(date, {zone: DEFAULT_TIME_ZONE}).toRelative() : null;

type FormatDateRangeProps = {
  range: [string?, string?];
  separator?: string;
  format?: string;
  options?: FormatDateOptions;
  fromFallback?: string;
  toFallback?: string;
};

export const formatDateRange = ({
  range,
  separator = '–', // typographically correct en dash
  format = DATE_ISO_FORMAT,
  options = {},
  fromFallback = '--',
  toFallback = '--',
}: FormatDateRangeProps): string => {
  const [from, to] = range;

  return [
    from ? formatDate(from, format, options) : fromFallback,
    to ? formatDate(to, format, options) : toFallback,
  ].join(` ${separator} `);
};

export const isSingleDayDateRange = (
  from: DateTime | null,
  to: DateTime | null,
) => {
  if (!from || !to) {
    return false;
  }

  return (
    from.hasSame(to, 'day') &&
    from.equals(from.startOf('day')) &&
    to.equals(to.endOf('day'))
  );
};

export const parseISOToDateTime = (value: string | null): DateTime | null => {
  if (!value) {
    return null;
  }
  const dt = DateTime.fromISO(value, {zone: 'utc'});
  return dt.isValid ? dt : null;
};

export const sanitizeDateTimeValues = <T>(values: T): T => {
  if (typeof values === 'function' || values === null) {
    return values;
  }
  return Object.fromEntries(
    Object.entries(values as Record<string, unknown>).map(([key, value]) => [
      key,
      value instanceof DateTime && !value.isValid ? null : value,
    ]),
  ) as T;
};

export const getValidISO = (
  dateTime: DateTime | null,
  options?: ToISOTimeOptions,
): string | null => {
  return dateTime?.isValid ? dateTime?.toISO(options) : null;
};

export const dateTimeISOStringParser = createParser({
  parse: value => parseISOToDateTime(value),
  serialize: value => getValidISO(value) || '',
});

export const dateISOStringParser = createParser({
  parse: value => parseISOToDateTime(value),
  serialize: value =>
    getValidISO(value, {precision: 'day', includeOffset: false}) || '',
});
